import dayJs from 'dayjs';

import { yup } from '@app/formik';
import { DynamicFormConfigSurvey, DynamicFormElementType, DynamicFormElementWidthMode } from '@app/dynamic';
import { COUNTRIES } from '@app/application/constants/countries';
import { USA_STATES } from '@app/user/constants/usaStates';
import { SexualOrientationEnum } from '@app/application/constants';
import { isPhoneValid } from '@app/auth/utils';
import {
  EnglishLevelEnum,
  GenderIdentityEnum,
  PronounsEnum,
  QuestionnaireDiagnosisEnum,
  QuestionnairePdf,
  QuestionnaireRelationshipStatusEnum,
  QuestionnaireSurveyEnum,
} from '../constants';
import cardImage from '../images/card-questionnaire-default.jpg';

export const firstIntendedParent: DynamicFormConfigSurvey<QuestionnaireSurveyEnum> = {
  name: QuestionnaireSurveyEnum.firstIntendedParent,
  meta: {
    cardImage,
    dependOnFields: ['status', 'isPartnerInformation', 'partner.firstName'],
  },
  pages: [
    {
      name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information`,
      information: true,
      elements: [
        {
          name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information`,
          type: DynamicFormElementType.information,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            translation: 'questionnaire',
            variables: {
              time: '5-10',
            },
            template: [['description.item1', 'description.item2', 'description.item3']],
          },
        },
      ],
    },
    {
      name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information.part1`,
      information: true,
      elements: [
        {
          name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information.part1`,
          type: DynamicFormElementType.information,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            translation: 'questionnaire',
            template: ['header'],
          },
        },
      ],
    },
    {
      name: 'name',
      elements: [
        {
          name: 'firstName',
          alias: 'legalFirstName',
          type: DynamicFormElementType.text,
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 50,
          },
        },
        {
          optional: true,
          name: 'middleName',
          type: DynamicFormElementType.text,
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            length: 50,
            withLockIcon: true,
          },
        },
        {
          name: 'lastName',
          alias: 'legalLastName',
          type: DynamicFormElementType.text,
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            length: 50,
            withLockIcon: true,
          },
        },
        {
          optional: true,
          name: 'nickname',
          type: DynamicFormElementType.text,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 50,
          },
        },
      ],
    },
    {
      name: 'cellPhone',
      elements: [
        {
          name: 'phone',
          type: DynamicFormElementType.phone,
          widthMode: DynamicFormElementWidthMode.full,
          validIf: value => value.length >= 12,
          validation: (value: any, t: (key: string) => string) => {
            try {
              yup
                .string()
                .nullable()
                .test(
                  'optional-or-phone',
                  t('general.validation.phoneNumber') as string,
                  isPhoneValid(t, 'general.validation.phoneNumber'),
                )
                .validateSync(value);
              return undefined;
            } catch (error) {
              return (error as Error).message;
            }
          },
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            disableDropdown: false,
            forceCallingCode: false,
            withLockIcon: true,
            length: 13,
          },
        },
      ],
    },
    {
      name: 'email',
      elements: [
        {
          name: 'contactEmail',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          validIf: value => yup.string().email().isValidSync(value),
          validation: (value: any, t: (key: string) => string) => {
            try {
              const ans = yup.string().email().isValidSync(value);
              if (!ans) {
                throw new Error(t('general.validation.email'));
              }
              return undefined;
            } catch (error) {
              return (error as Error).message;
            }
          },
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            withLockIcon: true,
            capitalize: false,
          },
        },
      ],
    },
    {
      name: 'dateOfBirthAndAge',
      elements: [
        {
          name: 'dateOfBirth',
          type: DynamicFormElementType.date,
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            withLockIcon: true,
            openTo: 'year',
            minDate: dayJs().subtract(80, 'years'),
            maxDate: dayJs().subtract(10, 'years'),
            views: ['year', 'month', 'day'],
          },
        },
        {
          optional: true,
          name: 'age',
          type: DynamicFormElementType.computedText,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            disabled: true,
            formula: ({ dateOfBirth }) => {
              return dateOfBirth && dateOfBirth !== 'Invalid Date'
                ? dayJs().diff(dayJs(dateOfBirth).subtract(1, 'day'), 'y')
                : '-';
            },
          },
        },
      ],
    },
    {
      name: 'place',
      elements: [
        {
          name: 'placeOfBirth',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 100,
          },
        },
      ],
    },
    {
      name: 'address',
      elements: [
        {
          name: 'country',
          type: DynamicFormElementType.autocomplete,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            options: COUNTRIES,
          },
        },
        {
          name: 'state',
          type: DynamicFormElementType.autocomplete,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ country }) => country === 'US',
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            options: USA_STATES,
          },
        },
        {
          name: 'fullAdress',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            length: 300,
            withLockIcon: true,
          },
        },
      ],
    },
    {
      name: 'employmentStatus',
      elements: [
        {
          name: 'isEmployed',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
        {
          name: 'occupation',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ isEmployed }) => Boolean(isEmployed),
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 100,
          },
        },
        {
          name: 'employer',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ isEmployed }) => Boolean(isEmployed),
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
            },
          },
          props: {
            withLockIcon: true,
            length: 100,
          },
        },
      ],
    },
    {
      name: 'english',
      elements: [
        {
          name: 'englishLevel',
          type: DynamicFormElementType.enumRadio,
          widthMode: DynamicFormElementWidthMode.half,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            enum: EnglishLevelEnum,
          },
        },
        {
          name: 'languages',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 300,
          },
        },
      ],
    },
    {
      name: 'gender',
      elements: [
        {
          name: 'genderIdentity',
          showDescription: true,
          type: DynamicFormElementType.enumSelect,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            enum: GenderIdentityEnum,
          },
        },
        {
          name: 'genderIdentityDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ genderIdentity }) => genderIdentity === GenderIdentityEnum.other,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 300,
          },
        },
        {
          name: 'pronouns',
          showDescription: true,
          type: DynamicFormElementType.enumSelect,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            enum: PronounsEnum,
          },
        },
        {
          name: 'pronounsDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ pronouns }) => pronouns === PronounsEnum.other,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 300,
          },
        },
      ],
    },
    {
      name: 'sexual',
      elements: [
        {
          name: 'sexualOrientation',
          showDescription: true,
          type: DynamicFormElementType.enumSelect,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            enum: SexualOrientationEnum,
          },
        },
        {
          name: 'sexualOrientationDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ sexualOrientation }) => sexualOrientation === SexualOrientationEnum.other,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 300,
          },
        },
      ],
    },
    {
      name: 'relationship',
      elements: [
        {
          name: 'relationshipStatus',
          type: DynamicFormElementType.enumSelect,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            enum: QuestionnaireRelationshipStatusEnum,
          },
        },
        {
          name: 'relationshipStatusDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ relationshipStatus }) => relationshipStatus === QuestionnaireRelationshipStatusEnum.other,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            length: 300,
          },
        },
        {
          name: 'isPartnerInformation',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.agency]: true,
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
        {
          name: 'partner.firstName',
          alias: 'secondParentFirstName',
          type: DynamicFormElementType.text,
          visibleIf: ({ isPartnerInformation }) => isPartnerInformation,
          props: {
            length: 50,
          },
        },
      ],
    },
    {
      name: 'isAnyOtherChildren',
      elements: [
        {
          noLabel: true,
          name: 'isAnyOtherChildren',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            defaultChecked: false,
          },
        },
        {
          name: 'otherChildren',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ isAnyOtherChildren }) => Boolean(isAnyOtherChildren),
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            multiline: true,
            length: 1000,
          },
        },
      ],
    },
    {
      name: 'isArrested',
      elements: [
        {
          noLabel: true,
          name: 'isArrested',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            defaultChecked: false,
            reversed: true,
          },
        },
        {
          name: 'arrestDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ isArrested }) => Boolean(isArrested),
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            multiline: true,
            length: 1000,
          },
        },
      ],
    },
    {
      name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information.part2`,
      information: true,
      elements: [
        {
          name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information.part2`,
          type: DynamicFormElementType.information,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            translation: 'questionnaire',
            template: ['header'],
          },
        },
      ],
    },
    {
      name: 'personalityDescription',
      elements: [
        {
          noLabel: true,
          name: 'personalityDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'interests',
      elements: [
        {
          noLabel: true,
          name: 'interests',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'favoriteGeneral',
      elements: [
        {
          noLabel: true,
          name: 'favoriteGeneral',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'favoriteFood',
      elements: [
        {
          noLabel: true,
          name: 'favoriteFood',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'religion',
      elements: [
        {
          noLabel: true,
          name: 'religion',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information.part3`,
      information: true,
      elements: [
        {
          name: `${QuestionnaireSurveyEnum.firstIntendedParent}.information.part3`,
          type: DynamicFormElementType.information,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            translation: 'questionnaire',
            template: ['header', ['description.item1', 'description.item2']],
          },
        },
      ],
    },
    {
      name: 'hasHospitalizedForPsychiatric',
      elements: [
        {
          noLabel: true,
          name: 'hasHospitalizedForPsychiatric',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            reversed: true,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
        {
          name: 'hospitalizationDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ hasHospitalizedForPsychiatric }) => Boolean(hasHospitalizedForPsychiatric),
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'haveAlcoholDrugIllnessHistory',
      elements: [
        {
          noLabel: true,
          name: 'haveAlcoholDrugIllnessHistory',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            reversed: true,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
        {
          name: 'alcoholDrugIllnessDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ haveAlcoholDrugIllnessHistory }) => Boolean(haveAlcoholDrugIllnessHistory),
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'haveHealthIssues',
      elements: [
        {
          noLabel: true,
          name: 'haveHealthIssues',
          type: DynamicFormElementType.booleanRadio,
          widthMode: DynamicFormElementWidthMode.full,
          props: {
            reversed: true,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
        {
          name: 'healthIssuesDescription',
          type: DynamicFormElementType.text,
          widthMode: DynamicFormElementWidthMode.full,
          visibleIf: ({ haveHealthIssues }) => Boolean(haveHealthIssues),
          props: {
            multiline: true,
            length: 1000,
          },
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
        },
      ],
    },
    {
      name: 'yourDiagnosis',
      elements: [
        {
          name: 'yourDiagnosis',
          type: DynamicFormElementType.toggleButtonGroup,
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            json: true,
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            translation: 'questionnaire',
            options: Object.values(QuestionnaireDiagnosisEnum),
          },
        },
        {
          name: 'yourDiagnosisDetails',
          type: DynamicFormElementType.text,
          visibleIf: ({ yourDiagnosis }) => yourDiagnosis && Object.values(yourDiagnosis).some(Boolean),
          widthMode: DynamicFormElementWidthMode.full,
          meta: {
            pdf: {
              [QuestionnairePdf.surrogates]: true,
            },
          },
          props: {
            multiline: true,
            length: 1000,
          },
        },
      ],
    },
  ],
};
